import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import AddNewButton from '../../../../components/AddNewButton';
import DropdownMenu from '../../../../components/DropdownMenu';
import { findSelected } from '../../../../components/DropdownMenu/util';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import SearchInputGroup from '../../../../components/SearchInputGroup';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { adminPageNewUserData, adminPageUpdateFilters, adminPageUpdateSearch, selectAdminPageFilters } from '../../../../redux/modules/adminPage';
import { resetFormIsDirty } from '../../../../redux/modules/form';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import { hideTray, showTray } from '../../../../redux/modules/tray';

const TabHeaderPanelSearch = ({ tabName, tabId, formComponent, icon }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const [selectedOption, setSelectedOption] = useState();
  const filters = useSelector(selectAdminPageFilters);
  const FormComponent = formComponent;

  const options = [
    {
      label: `Active ${tabName}`,
      id: 'active'
    },
    {
      label: tabName === 'Users' ? 'Inactive Users' : 'Archived Departments',
      id: 'inactive'
    },
    {
      label: `All ${tabName}`,
      id: 'all'
    }
  ];

  useEffect(() => {
    const currentFilter = filters && filters.find(f => f.name === tabId);
    const defaultSelected = currentFilter && options.find(o => o.id === currentFilter.selected);
    setSelectedOption(defaultSelected.label);
  }, [filters, options, tabId]);

  const handleViewClick = selected => {
    const selectedFilter = findSelected(options, selected.value);
    setSelectedOption(selectedFilter.label);
    dispatch(adminPageUpdateFilters(tabId, selectedFilter.id));
  };

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    dispatch(adminPageUpdateSearch(tabId, searchValue));
  };

  const handleCancelForm = () => {
    if (tabId === 'user') {
      dispatch(adminPageNewUserData({}));
    }

    dispatch(resetFormIsDirty());
    setTimeout(dispatch(hideModal()), 200);
    setTimeout(dispatch(hideTray()), 200);
  };

  const tabHeaderAnalyticsIds = useMemo(() => {
    switch (tabId) {
      case 'user':
        return {
          search: analyticsIds.adminSettingsPage.users.searchUsersInput,
          filter: analyticsIds.adminSettingsPage.users.filterByStatusDropdown,
          addButton: analyticsIds.adminSettingsPage.users.addNewUserButton
        };
      case 'department':
        return {
          search: analyticsIds.adminSettingsPage.departments.searchDepartmentsInput,
          filter: analyticsIds.adminSettingsPage.departments.filterByStatusDropdown,
          addButton: analyticsIds.adminSettingsPage.departments.addNewDepartmentButton
        };
      default:
        return {};
    }
  }, [tabId]);

  const handleFormClose = formTrayProps => {
    if (formTrayProps.formIsDirty) {
      dispatch(showModal(
        () => (
          <SaveChangesModal changedInfo="user information" />
        ),
        {
          title: 'Exit without Saving?',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              onClick: () => handleCancelForm(),
              dataAnalyticsId: 'cancel-user-creation'
            }
          }
        }
      ));
    } else {
      handleCancelForm();
    }
  };

  const showFormTray = () => {
    dispatch(showTray(
      () => (
        <FormComponent />
      ),
      {
        onClose: handleFormClose,
        params: { companyId: params.companyId },
        props: { formIsDirty: false }
      }
    ));
  };

  return (
    <Box pl={0} pr={0}>
      <Flex direction="row" justify="space-between" p="0.5px" w="100%">
        <DropdownMenu
          dataAnalyticsKey={tabHeaderAnalyticsIds?.filter}
          dusk={tabHeaderAnalyticsIds?.filter}
          options={options}
          selected={selectedOption}
          variant="secondary"
          width="239px"
          onSelect={handleViewClick}
        />
        <Flex justify="flex-end" ml={4} width="70%">
          <SearchInputGroup
            dataAnalyticsId={tabHeaderAnalyticsIds.search}
            dusk={tabHeaderAnalyticsIds.search}
            mr={4}
            variant="secondary"
            onChange={handleSearchChange}
          />
          <AddNewButton
            dataAnalyticsId={tabHeaderAnalyticsIds.addButton}
            icon={icon}
            minWidth="190px"
            tabName={`New ${tabId}`}
            onClick={showFormTray}
          />
        </Flex>
      </Flex>
    </Box>
  );
};

TabHeaderPanelSearch.propTypes = {
  tabId: PropTypes.string,
  tabName: PropTypes.string,
  formComponent: PropTypes.func,
  icon: PropTypes.func
};

export default TabHeaderPanelSearch;
